/**
 * Check if a tab element is at least partially visible within a container
 */
export const isTabVisible = (tabRect: DOMRect, containerRect: DOMRect): boolean => {
  return tabRect.left < containerRect.right && tabRect.right > containerRect.left;
};

/**
 * Calculate scroll state based on scroll position.
 * Returns an object with canScrollPrev and canScrollNext flags
 */
export const calculateScrollState = (
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number,
): { canScrollPrev: boolean; canScrollNext: boolean } => {
  const isAtStart = scrollLeft <= 0;
  const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1; // -1 for rounding

  return {
    canScrollPrev: !isAtStart,
    canScrollNext: !isAtEnd,
  };
};

/**
 * Find the next tab to scroll to
 * @param tabList - The container element (tablist)
 * @param tabElements - Array of all tab elements
 * @returns The next tab element to scroll to, or null if not found
 */
export const findNextTabToScroll = (tabList: HTMLElement | null, tabElements: HTMLElement[]): HTMLElement | null => {
  if (!tabList || tabElements.length === 0) return null;

  const containerRect = tabList.getBoundingClientRect();

  // Find the rightmost visible tab (or first partially visible on the right)
  for (let i = tabElements.length - 1; i >= 0; i--) {
    const tabRect = tabElements[i].getBoundingClientRect();
    if (isTabVisible(tabRect, containerRect)) {
      // This tab is visible, return next one if exists
      if (i + 1 < tabElements.length) {
        return tabElements[i + 1];
      }
    }
  }

  // If no visible tab found, return first tab
  return tabElements[0] || null;
};

/**
 * Find the previous tab to scroll to
 * @param tabList - The container element (tablist)
 * @param tabElements - Array of all tab elements
 * @returns The previous tab element to scroll to, or null if not found
 */
export const findPrevTabToScroll = (tabList: HTMLElement | null, tabElements: HTMLElement[]): HTMLElement | null => {
  if (!tabList || tabElements.length === 0) return null;

  const containerRect = tabList.getBoundingClientRect();

  // Find the leftmost visible tab (or first partially visible on the left)
  for (let i = 0; i < tabElements.length; i++) {
    const tabRect = tabElements[i].getBoundingClientRect();
    if (isTabVisible(tabRect, containerRect)) {
      // This tab is visible, return previous one if exists
      if (i - 1 >= 0) {
        return tabElements[i - 1];
      }
    }
  }

  // If no visible tab found, return last tab
  return tabElements[tabElements.length - 1] || null;
};
